import React, { FC, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, LabelSM, TitleXXS } from '@/atoms/Text'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { CatalogTitle, hasHydraIdAndSlug } from '@/services/ContentCatalog'
import { useGuildMember } from '@/services/GuildUserService'
import { Episode, Project } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface ContentShareCTAProps {
  episode?: Episode
  project?: Project
  moviePosterPath?: string | null
  setShowShareModal: (showShareModal: boolean) => void
  watchableTitle?: CatalogTitle | null
}

export const ContentShareCTA: FC<ContentShareCTAProps> = React.memo(
  ({ episode, project, moviePosterPath, setShowShareModal, watchableTitle }) => {
    const [hasDismissed, setHasDismissed] = useState(false)
    const { t } = useTranslate('watch')
    const track = useSafeTrack()
    const { playing, playingProgress } = useAngelPlayerContext()
    const { isGuildMember } = useGuildMember()

    const showShareBanner = Boolean(watchableTitle && hasHydraIdAndSlug(watchableTitle) && isGuildMember)

    const handleClick = () => {
      setShowShareModal(true)
      track('Clicked Guild Content Share Player CTA Button', {
        guid: episode?.guid,
        episode,
      })
    }

    const renderCtaTime = useMemo(() => {
      if (!episode?.source?.duration) {
        return null
      }
      if (episode?.source?.credits) {
        return episode.source.credits
      } else {
        return episode?.source.duration - 60
      }
    }, [episode?.source?.credits, episode?.source?.duration])

    const showButtonTime = useMemo(() => {
      return (renderCtaTime ?? 0) + 30
    }, [renderCtaTime])

    const variants = {
      open: {
        opacity: 1,
        y: 0,
        transition: {
          type: 'spring',
          bounce: 0.2,
          duration: 0.6,
        },
      },
      closed: { y: 40, opacity: 0, display: 'none' },
    }

    const movieTitle = project?.name
    const shouldRenderCta = returnShouldRenderCta(playingProgress, renderCtaTime)
    const showCta = returnShowCta(playing, playingProgress, showButtonTime, showShareBanner)

    if (!shouldRenderContentShareCta(shouldRenderCta, hasDismissed)) return null

    return (
      <div
        id="guild-player-button"
        className="[w-92%] absolute left-1/2 top-1/4 z-40 min-w-[350px] max-w-[480px] -translate-x-1/2 -translate-y-1/4 text-[10px] text-white sm:text-base md:top-1/2 md:w-[98%] md:-translate-y-1/2"
      >
        <motion.div
          animate={showCta ? 'open' : 'closed'}
          variants={variants}
          className="mt-4 flex items-center justify-center md:mt-0"
        >
          <div
            className="relative rounded-2xl p-1"
            style={{
              background: 'linear-gradient(to bottom right, #EDD7C6, #D6A177, #FEDEC6, #D48D59, #A66141, #612B12)',
            }}
          >
            <div
              className="absolute -right-3 -top-3 cursor-pointer rounded-full p-[2px]"
              style={{
                background: 'linear-gradient(to bottom right, #EDD7C6, #D6A177, #FEDEC6, #D48D59, #A66141, #612B12)',
              }}
              onClick={() => setHasDismissed(true)}
            >
              <div className="rounded-full bg-core-gray-900 p-1">
                <CloseIcon color="white" size={20} />
              </div>
            </div>
            <div className="flex flex-row gap-2 rounded-xl bg-core-gray-900 p-2 md:gap-4 md:p-4 se:p-1.5">
              <div className="shrink-0">
                <Image
                  src={getCloudinaryImageUrl({
                    path: moviePosterPath ?? '',
                    width: 150,
                    height: 225,
                    transforms: 'c_fill',
                  })}
                  alt={`${movieTitle} poster`}
                  width={83}
                  height={148}
                  className="rounded-xl"
                />
              </div>

              <div className="flex flex-col justify-between gap-3 md:gap-4 se:gap-2">
                <TitleXXS weight="semibold" className="md:photon-title-xs">
                  <Translate i18nKey="GuildJoinCTATitle" t={t} values={{ movieTitle }}>
                    Did you enjoy <i>{{ movieTitle }}</i>?
                  </Translate>
                </TitleXXS>
                <CaptionSM className="se:photon-caption-xs md:photon-caption-md">
                  <Translate i18nKey="shareCTAMessage" t={t} values={{ movieTitle }}>
                    As a Guild member, you can share {{ movieTitle }} with anyone for free! Share now!
                  </Translate>
                </CaptionSM>
                <Button onClick={handleClick} variant="white" className="text-core-gray-950 se:py-2">
                  <div className="flex min-h-6 flex-row items-center se:min-h-4">
                    <div className="lg:hidden">
                      <ShareOutlineIcon size={16} className="-mt-0.5" color={'black'} />
                    </div>
                    <div className="hidden lg:flex">
                      <ShareOutlineIcon size={24} className="-mt-0.5" color={'black'} />
                    </div>
                    <LabelSM className="md:photon-label-md pl-1">{t('shareFreeAccess', 'Share Free Access')}</LabelSM>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    )
  },
)

ContentShareCTA.displayName = 'React.memo(ContentShareCTA)'

function returnShowCta(
  playing: boolean,
  playingProgress: number,
  showButtonTime: number,
  showShareBanner: boolean,
): boolean {
  return showShareBanner && playing && playingProgress >= showButtonTime
}

function returnShouldRenderCta(playingProgress: number, renderCtaTime: number | null) {
  return playingProgress > (renderCtaTime ?? 0)
}

function shouldRenderContentShareCta(shouldRenderCta: boolean, hasDismissed: boolean): boolean {
  return shouldRenderCta && !hasDismissed
}
